
/**
 * Reads and builds wall-clock times in a named IANA zone.
 *
 * A javascript Date is an instant with no zone of its own: getHours(), getDate() and the
 * rest answer in whatever zone the browser happens to be in. The java time units read
 * every field through a Calendar built on the unit's own zone, so a date carrying
 * "America/Halifax" reports the Halifax hour wherever it runs. These helpers give the
 * javascript units the same behaviour through Intl, which is the only zone database the
 * platform exposes.
 */

const FIELD_FORMATTERS = new Map();

function formatterFor(timeZone) {
  let formatter = FIELD_FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    FIELD_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

const WEEKDAY_INDEX = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/** Falls back to the browser zone the way the java units fall back to the default zone. */
export function safeZone(timeZone) {
  if (!timeZone || timeZone === '0') {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
  try {
    formatterFor(timeZone);
    return timeZone;
  } catch (ignored) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }
}

/**
 * The wall-clock fields of an instant as seen in the given zone. Month is zero based to
 * match java's Calendar.MONTH and javascript's Date.getMonth; day is the calendar day of
 * the month, one based, the way a calendar prints it.
 */
export function partsIn(date, timeZone) {
  const zone = safeZone(timeZone);
  const parts = formatterFor(zone).formatToParts(date);
  const found = {};
  for (const part of parts) {
    found[part.type] = part.value;
  }
  return {
    year: parseInt(found.year, 10),
    month: parseInt(found.month, 10) - 1,
    day: parseInt(found.day, 10),
    hour: parseInt(found.hour, 10) % 24,
    minute: parseInt(found.minute, 10),
    second: parseInt(found.second, 10),
    milli: date.getTime() >= 0 ? date.getTime() % 1000 : ((date.getTime() % 1000) + 1000) % 1000,
    weekday: WEEKDAY_INDEX[found.weekday] ?? 0,
  };
}

/** How far the zone sits from UTC, in milliseconds, at the given instant. */
export function offsetAt(timestamp, timeZone) {
  const parts = partsIn(new Date(timestamp), timeZone);
  const asUtc = Date.UTC(
    parts.year, parts.month, parts.day,
    parts.hour, parts.minute, parts.second, parts.milli,
  );
  return asUtc - timestamp;
}

/**
 * The instant at which the given zone reads the given wall clock. Two passes: the first
 * guesses using the offset at the guessed instant, the second corrects it where that guess
 * landed on the far side of a daylight saving change, which is the only case where the
 * offset used to build the guess is not the offset actually in force.
 */
export function timestampOf(fields, timeZone) {
  const zone = safeZone(timeZone);
  const wallClock = Date.UTC(
    fields.year,
    fields.month,
    fields.day,
    fields.hour || 0,
    fields.minute || 0,
    fields.second || 0,
    fields.milli || 0,
  );
  const firstPass = wallClock - offsetAt(wallClock, zone);
  const secondPass = wallClock - offsetAt(firstPass, zone);
  return secondPass;
}

/** A copy of an instant with some of its wall-clock fields replaced, read in its zone. */
export function withFields(date, timeZone, changes) {
  const zone = safeZone(timeZone);
  const parts = partsIn(date, zone);
  return new Date(timestampOf({ ...parts, ...changes }, zone));
}

/** The zone's short name for an instant, e.g. EST or EDT, matching the java short name. */
export function zoneShortName(date, timeZone) {
  const zone = safeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    timeZoneName: 'short',
  }).formatToParts(date);
  const name = parts.find((part) => part.type === 'timeZoneName');
  return name ? name.value : '';
}

/** The number of days in a month, the way DateUtils.daysInMonth answers it in java. */
export function daysInMonth(year, month) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

export default {
  safeZone,
  partsIn,
  offsetAt,
  timestampOf,
  withFields,
  zoneShortName,
  daysInMonth,
};
